import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, CheckCircle2 } from "lucide-react";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";
import { evaluatePassword } from "@/lib/passwordPolicy";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Página pública de activación de cuenta:
 *   /activar-cuenta?token=...
 * El admin envía el enlace; el usuario define su contraseña sin que el admin
 * la vea ni la escriba.
 */
export default function ActivateAccountPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Sin datos del usuario aquí (token opaco), validamos solo política básica.
  const evalRes = evaluatePassword(pwd);
  const canSubmit = token && pwd && pwd === confirm && evalRes.is_valid && !loading;

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-sm border-red-200 rounded-sm">
          <CardContent className="p-6 text-sm">
            <p className="text-red-700 font-semibold">Enlace inválido o sin token.</p>
            <p className="text-slate-500 mt-2 text-xs">Solicita un nuevo enlace al administrador.</p>
            <Link to="/login" className="block mt-4 text-xs uppercase tracking-wider text-slate-700 underline">
              Volver al inicio de sesión
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-sm border-emerald-200 rounded-sm" data-testid="activate-success">
          <CardContent className="p-6 text-center space-y-3">
            <div className="w-12 h-12 mx-auto rounded-sm bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-emerald-700" />
            </div>
            <p className="text-sm font-semibold text-slate-900">¡Cuenta activada!</p>
            <p className="text-xs text-slate-500">Ya puedes iniciar sesión con tu nueva contraseña.</p>
            <Button onClick={() => navigate("/login")} className="bg-slate-900 text-white uppercase text-xs tracking-wider h-9 rounded-sm">
              Ir a inicio de sesión
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== confirm) { toast.error("Las contraseñas no coinciden"); return; }
    if (!evalRes.is_valid) { toast.error(evalRes.reasons[0] || "Contraseña insegura"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/activate-account`, { token, new_password: pwd });
      setSuccess(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo activar la cuenta");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4"
         data-testid="activate-account-page">
      <Card className="w-full max-w-md rounded-sm">
        <CardContent className="p-6 space-y-4">
          <h1 className="heading-industrial text-lg font-bold uppercase">Define tu contraseña</h1>
          <p className="text-xs text-slate-500">
            Estás activando tu acceso a ASCAUTO. Crea una contraseña segura que cumpla nuestra política.
          </p>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label htmlFor="p1" className="text-xs uppercase tracking-wider">Nueva contraseña</Label>
              <Input id="p1" type="password" value={pwd} maxLength={128}
                     onChange={(e) => setPwd(e.target.value)}
                     data-testid="activate-pwd-input" autoFocus />
              <PasswordStrengthMeter password={pwd} />
            </div>
            <div>
              <Label htmlFor="p2" className="text-xs uppercase tracking-wider">Repite la contraseña</Label>
              <Input id="p2" type="password" value={confirm} maxLength={128}
                     onChange={(e) => setConfirm(e.target.value)}
                     data-testid="activate-confirm-input" />
              {confirm && confirm !== pwd && (
                <p className="text-[11px] text-red-600 mt-1">No coinciden.</p>
              )}
            </div>
            <Button type="submit" disabled={!canSubmit}
                    className="w-full bg-slate-900 hover:bg-black text-white uppercase tracking-wider text-xs h-10 rounded-sm"
                    data-testid="activate-submit-btn">
              <KeyRound className="w-3.5 h-3.5 mr-2" />
              {loading ? "Activando…" : "Activar mi cuenta"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
